import i18n from "../i18n";

/** Helpers for filter values that may arrive as a string, a list, or nothing. */

/** A multi-select filter value as a clean list of strings. */
export function toList(value: unknown): string[] {
  if (Array.isArray(value)) return value.map(String).filter(Boolean);
  if (typeof value === "string" && value.trim()) return [value];
  return [];
}

/** Case-insensitive "contains" match for filtering static option lists. */
export function matchesQuery(label: string, query: string): boolean {
  const q = (query || "").trim().toLowerCase();
  return !q || label.toLowerCase().includes(q);
}

/**
 * A listing's chip filter keeps a fixed word as its value ("Active", "Unread"…) and shows the translated
 * label, so a filter saved in one language still matches after the language changes.
 *
 * Filters saved before that held the label itself ("Active" or "सक्रिय"). `canonical` reads those back:
 * any value equal to a label of one of the given translation keys, in any of the app's languages, becomes
 * that key's fixed value. Values that are already fixed pass through unchanged.
 *
 *   const status = canonicalFilterValues(filters.status, { Active: "staffs:statusOptions.active", … });
 *   status.includes("Active")
 */
export function canonicalFilterValues(values: unknown, keys: Record<string, string>): string[] {
  const list = Array.isArray(values) ? values.map(String) : values == null || values === "" ? [] : [String(values)];
  if (!list.length) return list;
  const byLabel = new Map<string, string>();
  for (const [fixed, key] of Object.entries(keys)) {
    for (const lng of i18n.options.supportedLngs || ["en", "hi"]) {
      if (lng === "cimode") continue;
      const label = i18n.t(key, { lng, defaultValue: "" });
      if (label) byLabel.set(label, fixed);
    }
  }
  return Array.from(new Set(list.map((v) => (v in keys ? v : (byLabel.get(v) ?? v)))));
}

/**
 * A listing's saved filters (localStorage "filters:<scope>") with `field` read through
 * canonicalFilterValues, written back so the filter panel shows the right chips selected too.
 */
export function loadCanonicalFilters(storageKey: string, field: string, keys: Record<string, string>) {
  try {
    const saved = localStorage.getItem(storageKey);
    if (!saved) return {};
    const filters = JSON.parse(saved) ?? {};
    if (filters[field] == null) return filters;
    const fixed = { ...filters, [field]: canonicalFilterValues(filters[field], keys) };
    localStorage.setItem(storageKey, JSON.stringify(fixed));
    return fixed;
  } catch {
    return {};
  }
}
